import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Router, CanActivateChild } from '@angular/router';
import { environment } from 'src/environments/environment';
import { ACCESS_TOKEN_KEY, AuthService, REFRESH_TOKEN_KEY, SEBValidatorItem } from './api/auth.service';
import { EXCHANGE_CODE_KEY } from './api/remote-proctoring.service';
import { RoutesService } from './api/routes.service';

const ALLOWED_ROUTES = [
  '/test-taker/dashboard',
  '/test-taker/test-runner',
  '/test-admin/invigilate',
  '/test-taker/hardware-performance-assessment'
]

@Injectable({
  providedIn: 'root'
})
export class AltDomainTrafficGuard implements CanActivate, CanActivateChild {

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
    private auth: AuthService,
    private routes: RoutesService
  ) {}

  async canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    console.log('🔒 canActivateChild fired:', state.url);
    return this.canActivate(next, state);
  }

  async canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Promise<boolean | UrlTree> {
    console.log('🔒 canActivate fired:', state.url);
    const isAltDomain = environment.isAltDomain;

    // access transfer logic - receiving from URL then cleanup
    const SEBValidatorStatus = next.queryParamMap.get(SEBValidatorItem);
    const xCode = next.queryParamMap.get(EXCHANGE_CODE_KEY);

    if (xCode) {
      try {
        const res = await this.auth.apiCreate(this.routes.AUTH_EXCH_CODE, {code: xCode});

        // add the SEB validator
        if (SEBValidatorStatus) {
          this.auth.setSEBStatus(Number(SEBValidatorStatus));
        }

        if (res.accessToken) {
          localStorage.setItem(ACCESS_TOKEN_KEY, res.accessToken);
        }

        // 2. Save Refresh Token (if present)
        if (res.refreshToken) {
          localStorage.setItem(REFRESH_TOKEN_KEY, res.refreshToken);
        }

         // Force AuthService to acknowledge the new token
        try {
          await this.auth.reauth();
        } catch (e) {
          console.warn('Token transfer re-auth failed', e);
          // If this fails, the user will likely be kicked to login anyway,
          // but we proceed to let the natural flow handle it.
        }

        // Clean the URL: Remove the token so it doesn't stay in the address bar
        // We return false here to cancel the current navigation, then immediately
        // navigate to the same URL without the query param.
        const cleanUrl = state.url.split('?')[0]; //gives /en/test-taker/dashboard
        this.router.navigateByUrl(cleanUrl);
        return false;


      } catch (error) {
        console.warn('Exchange code failed', error);
      }
    }

    // traffic control
    if (isAltDomain) {
      // const isAllowedRoute = state.url.includes('/test-taker/dashboard') || state.url.includes('/test-taker/test-runner') || state.url.includes('/test-admin/invigilate');
      const isAllowedRoute = ALLOWED_ROUTES.some(route => state.url.includes(route));

      if (!isAllowedRoute) {
        await this.redirectToMain(environment.remoteRedirectUrl, state.url);
        return false;
      }
    }
    return true;
  }

  // Helper to bundle exchange-code + seb validator and redirect
  private async redirectToMain(domainUrl: string, path: string) {
    const res = await this.auth.apiCreate(this.routes.AUTH_EXCH_CODE, {action: 'generate'});
    const SEBValidator = localStorage.getItem(SEBValidatorItem);

    let destination = `${domainUrl}/#${path}`;
    const connector = destination.includes('?') ? '&' : '?';

    // Append code safely
    destination += `${connector}${EXCHANGE_CODE_KEY}=${encodeURIComponent(res.code || '')}`;

    if (SEBValidator) {
      destination += `&${SEBValidatorItem}=${encodeURIComponent(SEBValidator)}`;
    }

    this.document.location.href = destination;
  }

}
